import { useState } from 'react';
import Styles from './APIHeadersTable.module.scss';
import {
    Table, TableBody, TableCell, TextField,
    TableContainer, TableHead, TableRow, Paper, IconButton, Button, Tooltip
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteIcon from '@mui/icons-material/Delete';
import { listKeyGenerator } from 'utils/helpers/listKeyGenerator';
import { useTranslation } from 'components/lib';

export function APIHeadersTable({ reqHeaders, setReqHeaders, isDisabled }) {

    const [headerListKeys, setHeaderListKeys] = useState([]);

    function createData(headerKey, headerValue) {
        return { headerKey, headerValue };
    }

    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');

    const inputChange = (changeFunc, value) => {
        changeFunc(value)
    };

    const { t } = useTranslation();

    const onAddHeader = () => {
        if (newKey.length > 0 && newValue.length > 0) {
            setHeaderListKeys(prevList => [...prevList, listKeyGenerator(1)[0]]);
            setReqHeaders(preHeaders => {
                const newDataList = [...preHeaders];
                newDataList.push(createData(newKey, newValue));
                setNewKey('');
                setNewValue('');
                return newDataList;
            })
        }
    };

    const updateRows = (index, key, value) => {
        setReqHeaders(prevDataList => {
            const newDataList = [...prevDataList];
            newDataList[index] = { ...newDataList[index], [key]: value };
            return newDataList;
        });
    };

    const deleteRow = (index) => {
        setReqHeaders(prevDataList => {
            const newDataList = [...prevDataList];
            newDataList.splice(index, 1);
            return newDataList;
        });
        setHeaderListKeys(prevList => {
            const newList = [...prevList];
            newList.splice(index, 1);
            return newList;
        });
    };

    const clearAllRows = () => {
        setReqHeaders([]);
        setHeaderListKeys([]);
    };

    return (
        <div id='headersParent' style={{
            height: '100%',
            width: '100%',
        }}
        >
            <TableContainer component={Paper}>
                <Table
                    sx={{ minWidth: 650 }}
                    aria-label={t('Components.NodeLayout.NodeTopostman.headers.aria.table')}
                    size="small"

                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ borderRight: "1px solid lightgray;", fontWeight: 600, width: '50%' }}>
                                {t('Components.NodeLayout.NodeTopostman.headers.table.key')}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{ fontWeight: 600, width: '50%' }}
                            >
                                {t('Components.NodeLayout.NodeTopostman.headers.table.value')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reqHeaders.map((row, index) => (
                            <TableRow
                                key={headerListKeys[index]}
                            >
                                <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                        borderRight: "1px solid lightgray;",
                                        bgcolor: "#e1f5fe",
                                        paddingLeft: "14px"
                                    }}
                                >
                                    <input
                                        type="text"
                                        value={row.headerKey}
                                        className={Styles.inputBoxes}
                                        onChange={(e) => updateRows(index, 'headerKey', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell
                                    scope="row"
                                    sx={{
                                        bgcolor: "#e1f5fe",
                                        paddingLeft: "14px"
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px'
                                    }}>
                                        <input
                                            type="text"
                                            value={row.headerValue}
                                            className={Styles.inputBoxes}
                                            onChange={(e) => updateRows(index, 'headerValue', e.target.value)}
                                            style={{ flex: 1 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => deleteRow(index)}
                                            sx={{
                                                color: '#d32f2f',
                                                padding: '4px',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                                }
                                            }}
                                            title={t('Components.NodeLayout.NodeTopostman.headers.delete')}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <div id='addHeaderInputs' className={Styles.addHeaderInputs}>
                <TextField
                    disabled={isDisabled}
                    id={`input-key`}
                    placeholder={t('Components.NodeLayout.NodeTopostman.headers.placeholder.key')}
                    sx={{ width: '100%' }}
                    value={newKey}
                    size="small"
                    onChange={(e) => inputChange(setNewKey, e.target.value)}
                    inputProps={{ maxLength: 50 }}
                />
                <TextField
                    disabled={isDisabled}
                    id={`input-value`}
                    placeholder={t('Components.NodeLayout.NodeTopostman.headers.placeholder.value')}
                    sx={{ width: '100%' }}
                    value={newValue}
                    size="small"
                    onChange={(e) => inputChange(setNewValue, e.target.value)}
                    inputProps={{ maxLength: 500 }}
                />
            </div>
            <div id='addHeaderBtnContainer' className={Styles.addHeaderBtnContainer}>
                <Tooltip title={t('Components.NodeLayout.NodeTopostman.headers.tooltip')}>
                    <span>
                        <Button
                            id="resetBtn"
                            variant="outlined"
                            color="error"
                            disabled={reqHeaders.length === 0}
                            onClick={clearAllRows}
                            startIcon={<DeleteForeverIcon />}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                borderColor: '#d32f2f',
                                color: '#d32f2f',
                                '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                    borderColor: '#d32f2f',
                                },
                                '&.Mui-disabled': {
                                    color: 'rgba(0,0,0,0.26)',
                                    borderColor: 'rgba(0,0,0,0.12)',
                                },
                            }}
                        >
                            {t('Components.NodeLayout.NodeTopostman.headers.clearAllBtn')}
                        </Button>
                    </span>
                </Tooltip>
                <button
                    id='addBtn'
                    className={Styles.addBtn}
                    onClick={() => { onAddHeader() }}
                    disabled={newKey.length === 0 || newValue.length === 0}
                >
                    {t('Components.NodeLayout.NodeTopostman.headers.button')}
                </button>
            </div>
        </div >
    );
}